import {Box, Divider, Group, Stack, Switch, Text} from '@mantine/core';
import React, {useCallback} from 'react';

import {trackEvent} from 'analytics/Analytics';
import BenchlingConfig from 'BenchlingConfig';
import {LLM_CHAT_ANALYTICS_ACTIONS} from 'constants/analytics-products/LLMChatConstants';
import UserAgentGuidelines from 'llm/chat-library/agent-guidelines/UserAgentGuidelines';
import useUserAgentGuidelines from 'llm/chat-library/agent-guidelines/useUserAgentGuidelines';
import useUserAboutMe from 'llm/chat-library/sidepanel/useUserAboutMe';
import {
  DEEP_RESEARCH_EMAIL_NOTIFICATION_ENABLED_PREFERENCE_KEY,
  DEFAULT_DEEP_RESEARCH_EMAIL_NOTIFICATION_ENABLED,
} from 'llm/deep-research/DeepResearchConstants';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import usePreference from 'ui-library/hooks/usePreference';
import {CenteredSpinner} from 'ui-library/Spinner';

interface AISettingsGeneralProps {
  onRestartOnboarding?: () => void;
}

export default function AISettingsGeneral({onRestartOnboarding}: AISettingsGeneralProps): JSX.Element {
  const [emailNotificationEnabledPref, setEmailNotificationEnabled] = usePreference<boolean | undefined>(
    DEEP_RESEARCH_EMAIL_NOTIFICATION_ENABLED_PREFERENCE_KEY
  );
  const emailNotificationEnabled =
    emailNotificationEnabledPref ?? DEFAULT_DEEP_RESEARCH_EMAIL_NOTIFICATION_ENABLED;

  const {guidelines, isLoading, saveGuidelines} = useUserAgentGuidelines();
  const {aboutMe, isLoading: isAboutMeLoading, clearAboutMe} = useUserAboutMe();

  const handleClearAboutMe = useCallback(() => {
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.CLEARED_ONBOARDING_ABOUT_ME);
    clearAboutMe();
  }, [clearAboutMe]);

  const handleStartOnboarding = useCallback(() => {
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.STARTED_ONBOARDING_FROM_SETTINGS);
    onRestartOnboarding?.();
  }, [onRestartOnboarding]);

  const handleRedoOnboarding = useCallback(() => {
    trackEvent(LLM_CHAT_ANALYTICS_ACTIONS.RESTARTED_ONBOARDING_FROM_SETTINGS);
    onRestartOnboarding?.();
  }, [onRestartOnboarding]);

  return (
    <Stack spacing={24}>
      <Box>
        <Text fw="bold" size="xl">
          General
        </Text>
        <Text fw="normal" size="sm" c="dimmed" mt={4}>
          Customize your AI experience to match your workflow.
        </Text>
      </Box>

      {/* Notifications Section */}
      <Stack spacing={4}>
        <Text fw="bold" size="md">
          Notifications
        </Text>
        <Switch
          styles={{
            label: {
              // For some reason, the label is bold by default, we want it to be normal here.
              fontWeight: 'normal',
            },
          }}
          checked={emailNotificationEnabled}
          onChange={(event) => {
            setEmailNotificationEnabled(event.currentTarget.checked);
          }}
          labelPosition="left"
          label={
            BenchlingConfig.LLM_ENABLE_ASSISTANT_AGENT
              ? 'Notify me by email when reports complete'
              : 'Notify me by email when Deep Research reports complete'
          }
        />
      </Stack>

      <Divider />

      {/* Custom Instructions Section */}
      <Stack spacing={8}>
        <Text fw="bold" size="md">
          Custom Instructions
        </Text>
        {isLoading || guidelines == null ? (
          <CenteredSpinner />
        ) : (
          <UserAgentGuidelines value={guidelines} onSave={saveGuidelines} />
        )}
      </Stack>

      {/* About Me Section */}
      <>
        <Divider />

        <Stack spacing={8}>
          <Group position="apart" align="center">
            <Text fw="bold" size="md">
              About you
            </Text>
            {!isAboutMeLoading && aboutMe && (
              <Group spacing={4}>
                {onRestartOnboarding && (
                  <Button
                    buttonStyle={ButtonStyle.TEXT}
                    size={ButtonSize.SMALL}
                    text="Redo"
                    onClick={handleRedoOnboarding}
                    dataTestAttrs={{component: 'AISettingsGeneral', element: 'redo-onboarding-button'}}
                  />
                )}
                <Button
                  buttonStyle={ButtonStyle.TEXT}
                  size={ButtonSize.SMALL}
                  text="Clear"
                  onClick={handleClearAboutMe}
                  dataTestAttrs={{component: 'AISettingsGeneral', element: 'clear-about-me-button'}}
                />
              </Group>
            )}
          </Group>
          <Text fw="normal" size="sm" c="dimmed">
            Personalizes AI responses based on your role.
          </Text>
          {isAboutMeLoading ? (
            <CenteredSpinner />
          ) : aboutMe ? (
            <Box
              sx={(theme) => ({
                background: theme.colors.gray[0],
                borderRadius: 4,
                padding: '10px 12px',
                fontSize: 13,
                lineHeight: 1.5,
                color: theme.colors.gray[7],
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
              })}
            >
              {aboutMe}
            </Box>
          ) : (
            onRestartOnboarding && (
              <Button
                buttonStyle={ButtonStyle.SECONDARY}
                size={ButtonSize.SMALL}
                text="Start"
                onClick={handleStartOnboarding}
                dataTestAttrs={{component: 'AISettingsGeneral', element: 'start-onboarding-button'}}
              />
            )
          )}
        </Stack>
      </>
    </Stack>
  );
}
